import { useEffect, useState } from "react";
import { ArrowRight, Mail, Lock } from "lucide-react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useAuth } from "../../auth/AuthContext";
import { auth } from "../../services/auth";
import { ensurePush } from "../../services/push";
import { usePublicBrand } from "../../context/BrandContext";
import LeftPanel from "./LeftPanel";
import {
  FField,
  ErrBox,
  BtnPrimary,
  AuthLink,
  Spinner,
} from "./AuthComponents";

/* ── Mobile check only ───────────────────────────────────────────── */
function useIsMobile(breakpoint = 768) {
  const getValue = () =>
    typeof window !== "undefined" ? window.innerWidth <= breakpoint : false;

  const [isMobile, setIsMobile] = useState(getValue);

  useEffect(() => {
    const onResize = () => setIsMobile(getValue());
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [breakpoint]);

  return isMobile;
}

/* ── Logo zone — desktop kept same, only mobile reduced ─────────── */
function LogoZone({ publicBrand, isMobile }) {
  const [imgErr, setImgErr] = useState(false);
  const hasCustom = !!publicBrand?.logoUrl;
  const src = hasCustom ? publicBrand.logoUrl : "/assets/project_logo.png";

  return (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        marginBottom: isMobile ? 24 : 36,
        paddingBottom: isMobile ? 20 : 28,
        borderBottom: "1px solid #edf0f4",
      }}
    >
      {imgErr ? (
        <div
          style={{
            width: isMobile ? 60 : 72,
            height: isMobile ? 60 : 72,
            borderRadius: 16,
            background: "#f0f2f5",
            border: "1px solid #e5e7eb",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <span
            style={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: isMobile ? "1rem" : "1.1rem",
              color: "#111111",
              letterSpacing: "0.04em",
            }}
          >
            MDP
          </span>
        </div>
      ) : (
        <img
          src={src}
          alt="Logo"
          style={{
            height: isMobile ? 60 : 72,
            maxWidth: isMobile ? 170 : 200,
            objectFit: "contain",
          }}
          onError={() => setImgErr(true)}
        />
      )}
    </div>
  );
}

/* ── Main ───────────────────────────────────────────────────────────── */
export default function LoginPage() {
  const nav = useNavigate();
  const loc = useLocation();
  const sp = new URLSearchParams(loc.search);
  const inviteToken = sp.get("invite") || loc.state?.inviteToken || null;
  const sessionExpired = sp.get("expired") === "1";
  const { refresh } = useAuth();
  const publicBrand = usePublicBrand();
  const isMobile = useIsMobile();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState(sessionExpired ? "Your session has expired. Please sign in again." : null);
  const [isLoading, setIsLoading] = useState(false);

  function maybeRedirectToCanonicalDomain(me, desiredPath) {
    try {
      // Admins and managers always stay on the current host — never redirect them
      if (me?.role === "admin" || me?.role === "manager") return false;

      const host = window.location.hostname;
      const proto = window.location.protocol;
      const port = window.location.port ? `:${window.location.port}` : "";
      const isLocalhost =
        host === "localhost" ||
        host === "127.0.0.1" ||
        host.startsWith("localhost:");
      if (isLocalhost) return false;

      const mainBase =
        process.env.REACT_APP_PANEL_URL || "panel.mydesignerplus.com";
      const mainHosts = new Set([mainBase, `www.${mainBase}`]);

      let targetHost = null;

      if (
        me?.accountType === "organization" &&
        me?.primaryOrg?.branding?.dashboardDomain
      ) {
        const orgHost = me.primaryOrg.branding.dashboardDomain;
        if (orgHost && orgHost !== host) targetHost = orgHost;
      } else {
        if (!mainHosts.has(host)) targetHost = mainBase;
      }

      if (!targetHost || targetHost === host) return false;

      window.location.href = `${proto}//${targetHost}${port}${
        desiredPath || "/dashboard"
      }${window.location.search}${window.location.hash}`;
      return true;
    } catch {
      return false;
    }
  }

  async function handleSubmit(e) {
    e?.preventDefault();
    setErr(null);
    setIsLoading(true);
    try {
      if (inviteToken) {
        try {
          await fetch(
            `${process.env.REACT_APP_BACKEND_URL}/api/invites/${encodeURIComponent(
              inviteToken,
            )}/accept`,
            { method: "POST", credentials: "include" },
          );
          await refresh();
        } catch {}
      }

      const data = await auth.login(email, password);
      const loginUser = data.user ?? data;

      let me = null;
      try {
        const meRes = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/auth/me`,
          { credentials: "include", cache: "no-store" },
        );
        if (meRes.ok) me = await meRes.json();
      } catch {}

      await refresh();
      ensurePush().catch(() => {});

      const fallbackPath =
        loginUser?.role === "admin"
          ? "/admin"
          : loginUser?.role === "manager"
            ? "/ceed"
            : "/dashboard";

      const desiredPath = loc.state?.from?.pathname || fallbackPath;

      // Skip domain redirect for admins and managers — they always stay on current host
      if (
        me &&
        me.role !== "admin" &&
        me.role !== "manager" &&
        maybeRedirectToCanonicalDomain(me, desiredPath)
      )
        return;

      nav(desiredPath, { replace: true });
    } catch (e) {
      if (e.status === 412) {
        nav("/verify-otp?email=" + encodeURIComponent(email));
      } else {
        setErr(e?.data?.message || "Invalid credentials. Please try again.");
      }
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <div
      style={{
        display: "flex",
        minHeight: "100vh",
        fontFamily: "'DM Sans', sans-serif",
      }}
    >
      {/* LEFT — keep desktop exactly same, hide only on mobile */}
      {!isMobile && <LeftPanel />}

      {/* RIGHT — desktop same, mobile full width */}
      <div
        style={{
          width: isMobile ? "100%" : "50%",
          minHeight: "100vh",
          background: "#ffffff",
          borderLeft: isMobile ? "none" : "1px solid #e8ecf0",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: isMobile ? "24px 16px" : "48px 40px",
          overflowY: "auto",
          boxSizing: "border-box",
        }}
      >
        <div
          style={{
            width: "100%",
            maxWidth: 400,
          }}
        >
          {/* ── Logo ── */}
          <LogoZone publicBrand={publicBrand} isMobile={isMobile} />

          {/* ── Heading ── */}
          <div style={{ marginBottom: isMobile ? 24 : 32 }}>
            <div
              style={{
                fontFamily: "'DM Sans', sans-serif",
                fontSize: "0.62rem",
                letterSpacing: "0.14em",
                color: "#9ca3af",
                marginBottom: 8,
                textAlign: isMobile ? "center" : "left",
              }}
            >
              Account Login
            </div>
            <h2
              style={{
                fontFamily: "'DM Sans', sans-serif",
                fontSize: isMobile ? "1.9rem" : "clamp(2rem, 2.5vw, 2.6rem)",
                lineHeight: 1.1,
                letterSpacing: "0.01em",
                color: "#111827",
                margin: "0 0 10px",
                textAlign: isMobile ? "center" : "left",
              }}
            >
              Welcome Back
            </h2>
            <p
              style={{
                fontFamily: "'DM Sans', sans-serif",
                fontSize: "0.875rem",
                color: "#6b7280",
                margin: 0,
                lineHeight: 1.6,
                textAlign: isMobile ? "center" : "left",
              }}
            >
              Enter your credentials to access your workspace.
            </p>
          </div>

          {/* ── Form ── */}
          <ErrBox msg={err} />

          <form
            onSubmit={handleSubmit}
            style={{ display: "flex", flexDirection: "column", gap: 16 }}
          >
            <FField
              label="Email"
              icon={Mail}
              type="email"
              value={email}
              onChange={setEmail}
              placeholder="name@company.com"
              required
            />
            <div>
              <FField
                label="Password"
                icon={Lock}
                type="password"
                value={password}
                onChange={setPassword}
                placeholder="Enter your password"
                required
              />
              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  marginTop: 8,
                }}
              >
                <Link
                  to="/forgot-password"
                  style={{
                    fontFamily: "'DM Sans'",
                    fontSize: "0.82rem",
                    color: "#111111",
                    textDecoration: "none",
                  }}
                >
                  Forgot password?
                </Link>
              </div>
            </div>

            <BtnPrimary
              type="submit"
              disabled={isLoading}
              style={{ marginTop: 4 }}
            >
              {isLoading ? (
                <>
                  <Spinner /> Signing in…
                </>
              ) : (
                <>
                  Sign In <ArrowRight size={15} />
                </>
              )}
            </BtnPrimary>
          </form>

          {/* ── Divider + Create account ── */}
          <div style={{ height: 1, background: "#edf0f4", margin: "24px 0" }} />

          <p
            style={{
              textAlign: "center",
              fontFamily: "'DM Sans'",
              fontSize: "0.875rem",
              color: "#6b7280",
              margin: 0,
            }}
          >
            Don't have an account?{" "}
            <AuthLink to="/register">Create account</AuthLink>
          </p>

          {/* ── T&C + Privacy ── */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 6,
              marginTop: 24,
              flexWrap: "wrap",
            }}
          >
            <Link
              to={`${process.env.REACT_APP_LANDING_URL}/terms`}
              style={{
                fontFamily: "'DM Sans'",
                fontSize: "0.75rem",
                color: "#9ca3af",
                textDecoration: "none",
                transition: "color 0.15s",
              }}
              onMouseEnter={(e) => (e.currentTarget.style.color = "#6b7280")}
              onMouseLeave={(e) => (e.currentTarget.style.color = "#9ca3af")}
            >
              Terms &amp; Conditions
            </Link>
            <span style={{ color: "#d1d5db", fontSize: "0.75rem" }}>·</span>
            <Link
              to={`${process.env.REACT_APP_LANDING_URL}/privacy`}
              style={{
                fontFamily: "'DM Sans'",
                fontSize: "0.75rem",
                color: "#9ca3af",
                textDecoration: "none",
                transition: "color 0.15s",
              }}
              onMouseEnter={(e) => (e.currentTarget.style.color = "#6b7280")}
              onMouseLeave={(e) => (e.currentTarget.style.color = "#9ca3af")}
            >
              Privacy Policy
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
